/* ============================================
   Drop the Deck — UNO Card Interaction Logic
   ============================================ */
(function () {
  'use strict';

  function init(container) {
    if (!container) return;

    var grid = container.querySelector('[data-dtd-grid]');
    var tapHint = container.querySelector('[data-tap-hint]');
    var tapHintText = container.querySelector('[data-tap-hint-text]');
    var dots = Array.from(container.querySelectorAll('[data-dtd-dot]'));
    var wrappers = Array.from(container.querySelectorAll('[data-card-index]'));
    var cards = Array.from(container.querySelectorAll('[data-card]'));

    if (!wrappers.length) return;

    var mql = window.matchMedia('(max-width: 749px)');
    var state = {
      isMobile: mql.matches,
      currentIndex: 0,
      hintDismissed: false
    };

    var bgColors = wrappers.map(function (w) {
      return w.getAttribute('data-bg-color') || '#ffda00';
    });

    var flippedStack = [];
    var mobileObserver = null;
    var flipTimers = {};
    var cardRotations = {};

    /* Always flip in the same direction by incrementing rotation */
    function flipCard(card, idx) {
      if (cardRotations[idx] === undefined) cardRotations[idx] = 0;
      cardRotations[idx] += 180;
      card.style.transform = 'rotateY(' + cardRotations[idx] + 'deg)';
      card.classList.toggle('is-flipped');
    }

    /* ---- Entrance Animation + First Card Flip ---- */
    var hasEnteredViewport = false;

    function onSectionEnterViewport() {
      if (hasEnteredViewport) return;
      hasEnteredViewport = true;
      container.classList.add('is-visible');

      /* Auto-flip first card when section enters viewport */
      var firstCard = wrappers[0] && wrappers[0].querySelector('[data-card]');
      if (firstCard && !firstCard.classList.contains('is-flipped')) {
        flipTimers['entry-0'] = setTimeout(function () {
          flipCard(firstCard, 0);
          if (!state.isMobile) {
            flippedStack.push(0);
            updateDesktopBg();
          }
        }, 400);
      }
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            onSectionEnterViewport();
            observer.disconnect();
          }
        });
      }, { threshold: 0.15 });
      observer.observe(container);
    } else {
      onSectionEnterViewport();
    }

    /* ---- Update dots ---- */
    function updateDots() {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === state.currentIndex);
      });
    }

    function resetAllCards() {
      cards.forEach(function (card) {
        card.classList.remove('is-flipped', 'is-dropping', 'is-flipping-back');
        card.style.transform = '';
      });
      wrappers.forEach(function (w) {
        w.classList.remove('is-exiting', 'is-entering', 'is-active');
      });
      cardRotations = {};
    }

    /* ---- Mobile: swipe slider with auto-flip ---- */
    function setupMobile() {
      state.isMobile = true;
      state.currentIndex = 0;
      resetAllCards();

      // Clear any pending flip timers
      Object.keys(flipTimers).forEach(function (key) {
        clearTimeout(flipTimers[key]);
      });
      flipTimers = {};

      // Set initial bg color
      container.style.setProperty('--dtd-bg-color', bgColors[0]);
      updateDots();

      // Scroll to start — first card flip is handled by viewport observer
      grid.scrollLeft = 0;

      // Disconnect previous observer if any
      if (mobileObserver) mobileObserver.disconnect();

      mobileObserver = new IntersectionObserver(function (entries) {
        /* Wait until section is in viewport before flipping any cards */
        if (!hasEnteredViewport) return;

        entries.forEach(function (entry) {
          var wrapper = entry.target;
          var idx = parseInt(wrapper.getAttribute('data-card-index'), 10);
          var card = wrapper.querySelector('[data-card]');

          if (entry.isIntersecting) {
            if (idx !== state.currentIndex) {
              /* First card stays flipped permanently — only flip cards 2+ on scroll */
              if (idx !== 0 && !card.classList.contains('is-flipped')) {
                if (flipTimers[idx]) clearTimeout(flipTimers[idx]);

                flipTimers[idx] = setTimeout(function () {
                  flipCard(card, idx);
                }, 150);
              }

              state.currentIndex = idx;
            }

            // Update background and dots
            container.style.setProperty('--dtd-bg-color', bgColors[idx]);
            updateDots();
            dismissHint();
          } else {
            if (flipTimers[idx]) {
              clearTimeout(flipTimers[idx]);
              delete flipTimers[idx];
            }
            /* Flip back cards that leave focus — except card 1 which stays open */
            if (idx !== 0 && card.classList.contains('is-flipped')) {
              flipCard(card, idx);
            }
          }
        });
      }, {
        root: grid,
        threshold: 0.4
      });

      wrappers.forEach(function (w) {
        mobileObserver.observe(w);
      });

      if (tapHint && !state.hintDismissed) {
        tapHint.classList.remove('is-hidden');
      }
      if (tapHintText) tapHintText.textContent = 'Swipe to explore';
    }

    /* ---- Desktop ---- */
    function setupDesktop() {
      state.isMobile = false;
      resetAllCards();

      // Clear mobile observer
      if (mobileObserver) {
        mobileObserver.disconnect();
        mobileObserver = null;
      }

      // Clear flip timers
      Object.keys(flipTimers).forEach(function (key) {
        clearTimeout(flipTimers[key]);
      });
      flipTimers = {};

      container.style.removeProperty('--dtd-bg-color');
      container.classList.remove('has-active-card');
      flippedStack = [];

      if (tapHint && !state.hintDismissed) {
        tapHint.classList.remove('is-hidden');
      }
      if (tapHintText) tapHintText.textContent = 'Click to interact';
    }

    /* ---- Responsive switch ---- */
    function onMediaChange(e) {
      if (e.matches) {
        setupMobile();
      } else {
        setupDesktop();
      }
    }

    if (mql.addEventListener) {
      mql.addEventListener('change', onMediaChange);
    } else if (mql.addListener) {
      mql.addListener(onMediaChange);
    }

    if (state.isMobile) {
      setupMobile();
    } else {
      setupDesktop();
    }

    /* ---- Dismiss Tap Hint ---- */
    function dismissHint() {
      if (tapHint && !state.hintDismissed) {
        state.hintDismissed = true;
        tapHint.classList.add('is-hidden');
      }
    }

    /* ---- Desktop: update bg to the most recently flipped card ---- */
    function updateDesktopBg() {
      var color = flippedStack.length
        ? wrappers[flippedStack[flippedStack.length - 1]].getAttribute('data-bg-color') || '#ffda00'
        : '#ffffff';
      container.style.setProperty('--dtd-bg-color', color);
      container.classList.toggle('has-active-card', flippedStack.length > 0);
    }

    /* ---- Desktop Click (toggle) ---- */
    function handleDesktopClick(card, wrapper) {
      dismissHint();
      var idx = parseInt(wrapper.getAttribute('data-card-index'), 10);
      flipCard(card, idx);

      if (card.classList.contains('is-flipped')) {
        flippedStack.push(idx);
      } else {
        flippedStack = flippedStack.filter(function (i) { return i !== idx; });
      }

      updateDesktopBg();
    }

    /* ---- Event Delegation ---- */
    grid.addEventListener('click', function (e) {
      if (state.isMobile) return;

      var wrapper = e.target.closest('[data-card-index]');
      if (!wrapper) return;

      var card = wrapper.querySelector('[data-card]');
      if (card) handleDesktopClick(card, wrapper);
    });
  }

  /* ---- Initialize ---- */
  function boot() {
    var sections = document.querySelectorAll('[data-drop-the-deck]');
    sections.forEach(function (section) {
      init(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var container = e.target.querySelector('[data-drop-the-deck]');
    if (container) init(container);
  });
})();
